import { Link } from 'react-router-dom'
import { ShoppingBag, Loader2, Trash2, Plus, Minus, ArrowLeft } from 'lucide-react'
import { useCartStore } from '../context/store'
import { formatPrice } from '../utils/shopify'
import { useSeo } from '../hooks/useSeo'

export default function CartPage() {
  useSeo({ title: 'Your Cart', path: '/cart', noindex: true })
  const { lines, subtotal, currency, loading, updateItem, removeItem, checkout } = useCartStore()

  if (lines.length === 0) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-4">
      <ShoppingBag size={64} className="text-brand-purple opacity-30" />
      <h1 className="text-2xl font-bold">Your cart is empty</h1>
      <p className="text-brand-muted text-center">Add some products to get started</p>
      <Link to="/catalogue" className="btn-primary px-8 py-3">Browse Products</Link>
    </div>
  )

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <Link to="/catalogue" className="inline-flex items-center gap-2 text-brand-muted hover:text-white mb-6 text-sm transition-colors">
          <ArrowLeft size={16} /> Continue Shopping
        </Link>

        <h1 className="text-3xl font-bold mb-8">Your Cart</h1>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Items */}
          <div className="md:col-span-2 space-y-4">
            {lines.map(line => (
              <div key={line.id} className="flex gap-4 bg-brand-surface rounded-xl p-4">
                {line.image && (
                  <img src={line.image} alt={line.title} className="w-20 h-20 object-cover rounded-lg flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <Link to={`/products/${line.productId}`} className="font-medium hover:text-brand-purple transition-colors truncate block">
                    {line.title}
                  </Link>
                  {line.variantTitle && line.variantTitle !== 'Default Title' && (
                    <p className="text-sm text-brand-muted">{line.variantTitle}</p>
                  )}
                  <p className="text-brand-purple font-semibold mt-1">{formatPrice(line.price, currency)}</p>
                  <div className="flex items-center gap-3 mt-3">
                    <button
                      onClick={() => updateItem(line.variantId, line.quantity - 1)}
                      disabled={loading}
                      className="w-8 h-8 rounded-lg bg-brand-dark border border-white/10 flex items-center justify-center hover:border-brand-purple/50 transition-colors disabled:opacity-50"
                    >
                      <Minus size={14} />
                    </button>
                    <span className="w-6 text-center font-medium">{line.quantity}</span>
                    <button
                      onClick={() => updateItem(line.variantId, line.quantity + 1)}
                      disabled={loading}
                      className="w-8 h-8 rounded-lg bg-brand-dark border border-white/10 flex items-center justify-center hover:border-brand-purple/50 transition-colors disabled:opacity-50"
                    >
                      <Plus size={14} />
                    </button>
                    <button
                      onClick={() => removeItem(line.variantId)}
                      disabled={loading}
                      className="ml-auto p-2 hover:text-red-400 transition-colors disabled:opacity-50"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold">{formatPrice((parseFloat(line.price) * line.quantity).toString(), currency)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="bg-brand-surface rounded-xl p-6 h-fit">
            <h2 className="font-semibold mb-4">Order Summary</h2>
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-brand-muted">Subtotal</span>
                <span>{formatPrice(subtotal, currency)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-brand-muted">Shipping</span>
                <span className="text-brand-muted">Calculated at checkout</span>
              </div>
            </div>
            <div className="border-t border-white/10 pt-4 mb-6">
              <div className="flex justify-between font-semibold">
                <span>Total</span>
                <span className="text-brand-purple">{formatPrice(subtotal, currency)}</span>
              </div>
            </div>
            <button
              onClick={checkout}
              disabled={loading}
              className="w-full btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              Proceed to Checkout
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
